import { CanAccess } from '@modules/common/components/CanAccess';
import { ReasonPromptExamples } from '@modules/taxonomyV2/components/CreateReasonForm/ReasonPromptExamples';
import { getSelectedReasonDefinitions } from '@modules/taxonomyV2/helpers';
import {
  useGetReasonDefinitions,
  useGetReasonNameForRCs,
} from '@modules/taxonomyV2/hooks';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Category,
  PermissionOperation,
  PermissionResource,
  Rc,
} from 'generated/graphql';
import { useEffect, useRef, useState } from 'react';

interface CreateReasonStep1Props {
  prompt: string;
  selectedFeedbackRecordID?: string;
  selectedReasonDefinitions: Rc[];
  onPromptChange: (prompt: string) => void;
  onCancel: () => void;
  onCompleted: (args: {
    reasonName: string;
    category: Category;
    reasonDefinitions: Rc[];
  }) => void;
}

export function CreateReasonStep1({
  prompt,
  selectedFeedbackRecordID,
  selectedReasonDefinitions,
  onPromptChange,
  onCancel,
  onCompleted,
}: CreateReasonStep1Props) {
  const abortController = useRef(new AbortController());
  const headRef = useRef<HTMLDivElement | null>(null);

  const [headDimensions, setHeadDimensions] =
    useState<{ height: number; width: number }>();

  useEffect(() => {
    if (headRef.current) {
      setHeadDimensions({
        height: headRef.current.clientHeight,
        width: headRef.current.clientWidth,
      });
    }
  }, []);

  const [getReasonDefinitions, { loading: isFetchingReasonDefinitions }] =
    useGetReasonDefinitions({
      abortSignal: abortController.current.signal,
      selectedReasonDefinitions,
      selectedFeedbackRecordID,
    });
  const [getReasonNameForRC, { isLoading: isGeneratingReasonNameLoading }] =
    useGetReasonNameForRCs({
      abortSignal: abortController.current.signal,
    });

  const isLoading =
    isFetchingReasonDefinitions || isGeneratingReasonNameLoading;

  const moveToNextStep = async () => {
    if (!prompt) {
      return;
    }

    const { definitions } = await getReasonDefinitions(prompt);
    const { data, error } = await getReasonNameForRC(
      getSelectedReasonDefinitions(definitions),
    );

    if (error) {
      return;
    }

    const reasonData = data?.getReasonNameForCandidates;
    if (reasonData) {
      onCompleted({
        category: reasonData?.category,
        reasonName: reasonData?.displayName,
        reasonDefinitions: definitions,
      });
    }
  };

  const head = (
    <Stack
      ref={headRef}
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      mb={2}
    >
      <Typography variant="body1">Create Reason</Typography>
      <Stack direction="row" spacing={1}>
        <Button size="small" variant="outlined" onClick={() => onCancel()}>
          Cancel
        </Button>
        <CanAccess
          resource={PermissionResource.Taxonomy}
          operation={PermissionOperation.Write}
        >
          <LoadingButton
            color="primary"
            variant="contained"
            type="submit"
            size="small"
            disabled={!prompt || isLoading}
            onClick={moveToNextStep}
          >
            Next
          </LoadingButton>
        </CanAccess>
      </Stack>
    </Stack>
  );

  const promptSection = (
    <TextField
      autoFocus
      size="small"
      multiline
      rows={6}
      value={prompt}
      fullWidth
      variant="outlined"
      placeholder="Describe customer feedback where this Reason is present."
      helperText="We will use this as a prompt to find matching Reason Definitions for you to review."
      onChange={(event) => {
        onPromptChange(event.target.value);
      }}
      InputLabelProps={{ shrink: true }}
    />
  );

  return (
    <Stack spacing={1} height="100%">
      {head}
      <Stack
        spacing={2}
        sx={{
          height: `calc(100% - ${headDimensions?.height}px)`,
          overflowY: 'auto',
          mt: 3,
        }}
      >
        {isLoading ? (
          <Stack height="100%" justifyContent="center">
            <Typography variant="body2">
              Hang in there! We are curating Reason Definitions ✨
            </Typography>
            <Typography variant="caption">
              (This can take up to 30 seconds)
            </Typography>
            <LinearProgress sx={{ mt: 1 }} />
          </Stack>
        ) : (
          <>
            {promptSection}
            <ReasonPromptExamples
              width={headDimensions?.width || 0}
              height={325}
            />
          </>
        )}
      </Stack>
    </Stack>
  );
}
